import { setMetaData } from "@/plugins/misc"
import { canNavigate } from '@layouts/plugins/casl'
import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory } from 'vue-router'
import routes from '~pages'
import { useActivityLogStore } from '@/stores'
import { isUserLoggedIn, isUserPlatinum, isRestaurantAdminEligible, isPlatformAdmin } from './utils'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: () => {
        const isLoggedIn = isUserLoggedIn()

        return isLoggedIn ? '/discover/' : '/home/'
      },
    },

    //this makes sure that our component at src/pages/home/[...all].vue also handles /home
    {
      path: '/home',
      redirect: () => {
        const isLoggedIn = isUserLoggedIn()

        return isLoggedIn ? '/discover/' : '/home/'
      },
    },

    //this makes sure that our component at src/pages/favorites/[...all].vue also handles /favorites
    {
      path: '/discover',
      redirect: '/discover/',
    },

    //this makes sure that our component at src/pages/m/[...all].vue also handles /m
    {
      path: '/m',
      redirect: '/m/',
    },
    // The hardcoded /select/join, /verified/join, /friends/join and /join pages
    // from before the discount-group editor are gone. A group join URL is
    // /{publicId}/join (pages/[...all]/join.vue): when no discount group has
    // that public id the page sends the visitor to the home page, so those
    // old URLs land on /home/ until a group is created with that public id.
    // Bare /join has no public id to look up, so it redirects straight home.
    {
      path: '/join',
      redirect: '/home/',
    },

    // /admin root redirects to bookings
    {
      path: '/admin',
      redirect: '/admin/bookings',
    },

    // Legacy restaurant-admin URLs — redirect to the canonical /admin/* routes
    {
      path: '/restaurant-bookings',
      redirect: '/admin/bookings',
    },
    {
      path: '/ra-messages',
      redirect: '/admin/messaging',
    },

    ...setupLayouts(routes),
  ],
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach((to, _from, next) => {
  const isLoggedIn = isUserLoggedIn()

  // Default title and metadata - overriden by pages
  setMetaData('RM Preferred Reservations', 'trusted by Artists, Athletes, CEOs & Restaurants', 'https://reserverm.com/logo.png', 'https://reserverm.com')

  // Handle root path redirect based on authentication status
  if (to.path === '/') {
    return next(isLoggedIn ? '/discover/' : '/home/')
  }

  // Protect /discover routes - redirect to /home if not logged in
  if (to.path.startsWith('/discover') && !isLoggedIn) {
    return next('/home/')
  }

  // RM-331 (11/1): the Accept / Deny links in a group admin's email land here.
  // Signed out, go through sign-in and come back to the same page and request.
  if (to.path === '/group-members' && !isLoggedIn) {
    return next({ name: 'login', query: { to: to.fullPath } })
  }

  // Protect the Restaurant Account section (/admin/...) — require login and
  // restaurant admin eligibility. Match '/admin' + '/admin/...' ONLY: the
  // platform-admin dashboards are top-level '/admin-*' routes (/admin-comms,
  // /admin-reports, ...) guarded by their own ability meta, and must not be
  // caught by the mode gate below (that broke the admin E2E suite when
  // member-first login landed).
  if (to.path === '/admin' || to.path.startsWith('/admin/')) {
    if (!isLoggedIn) {
      return next({ name: 'login', query: { to: to.fullPath } })
    }

    if (!isRestaurantAdminEligible()) {
      // Non-eligible users are redirected with a reason query so the target page can show a toast.
      return next({ path: '/', query: { reason: 'not-authorized' } })
    }

    // Member mode never shows admin screens: /admin/* renders only when the
    // user has explicitly switched into Restaurant Account mode (the mode
    // switcher persists 'restaurantAdmin' before navigating here). Everyone
    // else — including eligible users following admin deep links while in
    // member mode — goes to the member home instead.
    const userId = (() => {
      try { return JSON.parse(localStorage.getItem('userData'))?._id } catch { return null }
    })()
    const storedMode = userId ? localStorage.getItem(`appMode:${userId}`) : null
    if (storedMode !== 'restaurantAdmin') {
      return next('/discover/')
    }
  }

  // Redirect if logged in and trying to access pages with redirectIfLoggedIn meta
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    return next("/")
  }

  // If not logged in and trying to access pages with allowLoggedInOnly meta
  // then redirect to login page
  if (to.meta.allowLoggedInOnly && !isLoggedIn) {
    return next({ name: 'login', query: { to: to.name !== 'index' ? to.fullPath : undefined } })
  }

  // Check for Platinum member requirement (for instant bookings checkout)
  if (to.meta.requiresPlatinum && isLoggedIn && !isUserPlatinum()) {
    return next({ path: '/plans', query: { reason: 'platinum-required' } })
  }

  // Require platform admin for admin-only authoring surfaces (tag admin, member
  // admin). Defense-in-depth: the API already enforces requireAdmin() on every
  // admin endpoint, but gating the route stops non-admins from ever loading the
  // page (and any stored HTML it would render). See H10.
  if (to.meta.requiresAdmin) {
    if (!isLoggedIn) {
      return next({ name: 'login', query: { to: to.fullPath } })
    }
    if (!isPlatformAdmin()) {
      return next({ name: 'not-authorized' })
    }
  }

  const canNav = canNavigate(to)
  if (!canNav) {
    // Redirect to home if not logged in (but don't redirect if going to a public page like login, register, etc.)
    if (!isLoggedIn && !to.meta.redirectIfLoggedIn) {
      return next('/home/')
    }

    // If logged in => not authorized
    if (isLoggedIn) {
      return next({ name: 'not-authorized' })
    }
  }

  return next()
})

// Lightweight page view tracking for the admin activity feed.
// Fires for both authenticated and anonymous visitors (server attributes via
// JWT when present, otherwise via the visitor_id cookie).
router.afterEach(to => {
  try {
    const store = useActivityLogStore()
    store.trackEvent('page_view', { path: to.path, name: to.name })
  } catch (e) {
    // Silent fail — never disrupt navigation
  }
})

export default router
